// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  defaultGetData,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Rating'>>;
  loading: boolean;
};

const pageId = adminPages.adminRatingsPage;

class AdminRatingsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionId !== null
          && data.RegionId === main.activeRegionId
          && data.modelName === 'Rating'
        ) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionId = main.activeRegionId;

    await defaultGetData({
      action: async () => {
        if (RegionId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyAdminRating({
            RegionId,
          });

          await this.setStateAsync({
            data: data.reverse(),
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async updateRatings(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionId = main.activeRegionId;

    await defaultGetData({
      action: async () => {
        if (RegionId !== null) {
          await Services.Api.utilityUpdateRatings({
            RegionId,
          });
        }
      },
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <Button
              color={'secondary'}
              id={'update-ratings-button'}
              onClick={() => this.updateRatings()}
              size={'sm'}
            >
              {t('Update ratings')}
            </Button>
          )}
          title={t('Ratings')}
        />
      </Card>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Entry')}</th>
            <th>{t('Type')}</th>
            <th>{t('Value')}</th>
            <th>{t('Date')}</th>
            <th className={'text-center'}>
              {t('Latest')}
            </th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Rating'>) => (
            <tr key={dataItem.id}>
              <td>
                {dataItem.RegistrationNo}
              </td>
              <td>
                {dataItem.Type}
              </td>
              <td>
                {`${dataItem.KeyValueName}: ${dataItem.KeyValue || ''}`}
              </td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td className={'text-center'}>
                {dataItem.Latest === true && (
                  <em className={'icon-check'} />
                )}
              </td>
              <td />
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRatingsPageView;
